/**
 * useOfferDuplicateValidation Hook
 * 
 * Checks if a user_id + partner_product_id + reward_id combination already exists.
 * Prevents duplicate reward assignments to the same user/partner combination.
 */

import { useState, useEffect } from 'react';
import { offerService } from '@/app/services/offerService';
import type { OfferResponse } from '@/app/types/offers';
import { logger } from '@/app/utils/logger';

interface DuplicateValidationResult {
  isDuplicate: boolean;
  existingOffer: OfferResponse | null;
  isLoading: boolean;
  error: string | null;
}

export function useOfferDuplicateValidation(
  userId: string | null,
  partnerProductId: number | null,
  rewardId: string | null
): DuplicateValidationResult {
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [existingOffer, setExistingOffer] = useState<OfferResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only validate if all three parameters are provided
    if (!userId || !partnerProductId || !rewardId) {
      setIsDuplicate(false);
      setExistingOffer(null);
      setError(null);
      return;
    }

    const checkDuplicate = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await offerService.queryOffers({
          user_id: userId,
          partner_product_id: partnerProductId,
          reward_id: rewardId,
          limit: 1,
          offset: 0,
        });

        if (result.offers.length > 0) {
          setIsDuplicate(true);
          setExistingOffer(result.offers[0] || null);
        } else {
          setIsDuplicate(false);
          setExistingOffer(null);
        }
      } catch (err) {
        logger.error('Failed to check for duplicate offer', { 
          error: err, 
          userId, 
          partnerProductId, 
          rewardId 
        });
        setError('Failed to validate duplicate offer');
        setIsDuplicate(false);
        setExistingOffer(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkDuplicate();
  }, [userId, partnerProductId, rewardId]);

  return {
    isDuplicate,
    existingOffer,
    isLoading,
    error,
  };
}

export default useOfferDuplicateValidation;

